/**
 * Build time configuration, validated once at module load.
 *
 * The variable names are the contract published by
 * `.github/workflows/deploy-hosting.yaml`. `vite.config.ts` already fails a
 * production build when a required one is missing; this second check exists
 * because `vite dev` deliberately does not fail, and a developer who has not
 * written a `.env.local` yet should get a readable configuration screen rather
 * than a stack trace from deep inside oidc-client-ts twenty seconds later.
 */

export interface AppConfig {
  /** Which deployment this bundle was built for: dev, staging or prod. */
  environment: string;
  /** Short commit of the build, so a bug report names a build. */
  commit: string | null;
  /** Tenant read and control API origin, no trailing slash. */
  apiBaseUrl: string;
  /** Ingest origin, interpolated into the onboarding snippet. */
  ingestHost: string;
  /** SDK bundle CDN origin, interpolated into the onboarding snippet. */
  cdnHost: string;
  /**
   * The replay player's own origin (D31). It is a separate hostname on purpose:
   * the player renders DOM that anyone with a public write key could have
   * uploaded, so it must never share an origin with the session token.
   */
  replaySandboxUrl: string;
  /** Cognito Managed Login origin, no trailing slash. */
  authDomain: string;
  /** ZeliSight's own user pool. Never a shared estate pool (plan D2). */
  userPoolId: string;
  /** Cognito issuer, derived from the pool id. Token issuer and JWKS host. */
  authAuthority: string;
  /** Public SPA app client id. PKCE, no secret. */
  authClientId: string;
  /**
   * The path segment the SDK is served from: an exact `v1.0.3` when a published
   * semver is configured, otherwise the floating `v1` alias.
   */
  sdkPath: string;
  /**
   * Subresource Integrity hash, present only when an exact version is pinned.
   * Null on the floating alias, where an integrity hash would break every
   * customer page on the next SDK release.
   */
  sdkSri: string | null;
}

export class ConfigError extends Error {
  readonly missing: readonly string[];

  constructor(missing: readonly string[]) {
    super(`Zeligate is missing build time configuration: ${missing.join(', ')}.`);
    this.name = 'ConfigError';
    this.missing = missing;
  }
}

function origin(value: string): string {
  const withScheme = /^https?:\/\//.test(value) ? value : `https://${value}`;
  return withScheme.endsWith('/') ? withScheme.slice(0, -1) : withScheme;
}

/**
 * Cognito's issuer is `https://cognito-idp.<region>.amazonaws.com/<poolId>`, and
 * the region is the part of the pool id before the underscore. Deriving it means
 * the region is never configured twice and can never disagree with the pool.
 */
export function issuerForPool(userPoolId: string): string | null {
  const separator = userPoolId.indexOf('_');
  if (separator <= 0 || separator === userPoolId.length - 1) return null;
  const region = userPoolId.slice(0, separator);
  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;
}

/** `1.0.3` becomes `v1.0.3`; an already prefixed `v1` is left alone. */
export function sdkPathSegment(value: string): string {
  return value.startsWith('v') ? value : `v${value}`;
}

function read(): AppConfig | ConfigError {
  const env = import.meta.env;

  const required: Record<string, string | undefined> = {
    VITE_ZS_ENV: env.VITE_ZS_ENV,
    VITE_ZS_API_BASE: env.VITE_ZS_API_BASE,
    VITE_ZS_INGEST_BASE: env.VITE_ZS_INGEST_BASE,
    VITE_ZS_CDN_BASE: env.VITE_ZS_CDN_BASE,
    VITE_ZS_AUTH_DOMAIN: env.VITE_ZS_AUTH_DOMAIN,
    VITE_ZS_USER_POOL_ID: env.VITE_ZS_USER_POOL_ID,
    VITE_ZS_USER_POOL_CLIENT_ID: env.VITE_ZS_USER_POOL_CLIENT_ID,
  };

  const missing = Object.entries(required)
    .filter(([, value]) => value === undefined || value.trim() === '')
    .map(([name]) => name);
  if (missing.length > 0) return new ConfigError(missing);

  const userPoolId = (required['VITE_ZS_USER_POOL_ID'] as string).trim();
  const authAuthority = issuerForPool(userPoolId);
  if (authAuthority === null) {
    // A pool id with no region prefix cannot produce an issuer, and guessing one
    // would fail later as an opaque JWKS 404 during sign in.
    return new ConfigError([`VITE_ZS_USER_POOL_ID (expected <region>_<id>, got "${userPoolId}")`]);
  }

  const rawVersion = env.VITE_ZS_SDK_VERSION?.trim();
  const rawSri = env.VITE_ZS_SDK_SRI?.trim();
  const sdkPath = sdkPathSegment(
    rawVersion !== undefined && rawVersion !== '' ? rawVersion : 'v1',
  );
  // An exact version looks like `v1.0.3`. The floating alias is `v1`.
  const pinned = /^v\d+\.\d+\.\d+/.test(sdkPath);

  return {
    environment: (required['VITE_ZS_ENV'] as string).trim(),
    commit: env.VITE_ZS_COMMIT?.trim() || null,
    apiBaseUrl: origin(required['VITE_ZS_API_BASE'] as string),
    ingestHost: origin(required['VITE_ZS_INGEST_BASE'] as string),
    cdnHost: origin(required['VITE_ZS_CDN_BASE'] as string),
    replaySandboxUrl: origin(
      (env['VITE_ZS_REPLAY_SANDBOX_URL'] as string | undefined) ?? '',
    ),
    authDomain: origin(required['VITE_ZS_AUTH_DOMAIN'] as string),
    userPoolId,
    authAuthority,
    authClientId: (required['VITE_ZS_USER_POOL_CLIENT_ID'] as string).trim(),
    sdkPath,
    sdkSri: pinned && rawSri !== undefined && rawSri !== '' ? rawSri : null,
  };
}

const result = read();

/** Non null when configuration is incomplete, so the shell can say what is missing. */
export const configError: ConfigError | null = result instanceof ConfigError ? result : null;

/**
 * Throws only if read before `configError` was checked. Every caller in the app
 * sits below the configuration gate in `App.tsx`, so this never throws in
 * practice, and if it ever does the message names the exact variable.
 */
export function getConfig(): AppConfig {
  if (result instanceof ConfigError) throw result;
  return result;
}
